import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/shared/ui/Card';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { useToast } from '@/shared/ui/Toast';
import { adSettingsApi } from '../../api/adSettingsApi';
import { useIsAgencyStaff } from '../../hooks';
import { budgetInput } from '../../utils/adSettingsForm';
import { budgetFigure, budgetSource, locationSplit } from '../../utils/adBudgetWords';
import { useAdSettings } from './useAdSettings';
import styles from './PaidSettings.module.css';

/**
 * The approved monthly ad budget: a read of what the practice approved in Ad Campaigns. Agency staff can override
 * it; the card then says so. Jenny keeps recommendations inside it, and each location's share of the practice total.
 */
export function AdBudgetCard({ locationId }: { locationId: string }) {
  const toast = useToast();
  const isAgency = useIsAgencyStaff();
  const { data, isLoading, save } = useAdSettings(locationId);
  const [override, setOverride] = useState<string | null>(null);

  if (isLoading || !data)
    return (
      <Card title="Approved monthly ad budget">
        <Spinner size="sm" />
      </Card>
    );

  const split = locationSplit(data);
  const apply = () => {
    const result = budgetInput(override ?? '');
    if (!result.ok) {
      toast.error(result.error);
      return;
    }
    save.mutate(() => adSettingsApi.overrideBudget(locationId, result.input), {
      onSuccess: () => setOverride(null),
    });
  };

  return (
    <Card
      title="Approved monthly ad budget"
      action={<Link to="/campaigns/list">Change it in Ad Campaigns</Link>}
    >
      <p className={styles.figure}>{budgetFigure(data)}</p>
      <p className={styles.hint}>{budgetSource(data)}</p>
      <p className={styles.intro}>
        Jenny keeps every recommendation inside this total. A change that would raise it comes to
        you as a budget request.
      </p>
      {split && <p className={styles.hint}>Location split: {split}.</p>}
      {isAgency && (
        <div className={styles.agency}>
          <span className={styles.agencyTag}>Agency only</span>
          {override === null ? (
            <Button variant="secondary" size="sm" onClick={() => setOverride('')}>
              Override budget…
            </Button>
          ) : (
            <>
              <Input
                label="New monthly budget ($), empty for none"
                inputMode="decimal"
                value={override}
                onChange={(e) => setOverride(e.target.value)}
              />
              <Button variant="primary" size="sm" onClick={apply} disabled={save.isPending}>
                Save override
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setOverride(null)}>
                Cancel
              </Button>
            </>
          )}
        </div>
      )}
    </Card>
  );
}
